/**
 * Terrain shading.
 *
 * Deliberately procedural for now — colour from altitude and slope, no
 * textures. It makes the geometry legible while the streaming is being built:
 * cliffs read as rock, flats as grass, and a wireframe-free surface still shows
 * whether the relief matches the real world. The landcover splat mask replaces
 * the flat/steep blend later.
 */

import { LANDCOVER } from '../core/sources/shortbread.js';
import { PLOWED_LANDCOVER } from '../core/weather/snow.js';
import type { Texture } from 'three';
import { PORTAL_INSIDE } from '../core/build/tunnelmesh.js';
import {
  ClampToEdgeWrapping,
  Color,
  DataTexture,
  DoubleSide,
  FrontSide,
  LinearFilter,
  MeshStandardMaterial,
  RedFormat,
  UnsignedByteType,
  Vector2,
  Vector4,
  type WebGLProgramParametersWithUniforms,
} from 'three';

/** 0 by day .. 1 at night: street lamps switch on at dusk. Set once a frame. */
export const floraNight = { value: 0 };

/**
 * World-space streetlamp light map, sampled directly by the terrain, road,
 * bridge/embankment, surface, building, vehicle, and character shaders so
 * lamplight falls onto the actual 3D geometry without floating in the air.
 */
const LAMP_MAP_SIZE = 2048;
const LAMP_MAP_EXTENT = 4096;
const LAMP_RADIUS_M = 14;
const LAMP_TEXEL_M = LAMP_MAP_EXTENT / LAMP_MAP_SIZE;
const LAMP_STAMP_R = Math.ceil(LAMP_RADIUS_M / LAMP_TEXEL_M);
const LAMP_STAMP_W = LAMP_STAMP_R * 2 + 1;
const LAMP_STAMP = (() => {
  const out = new Uint8Array(LAMP_STAMP_W * LAMP_STAMP_W);
  for (let dz = -LAMP_STAMP_R; dz <= LAMP_STAMP_R; dz++) {
    for (let dx = -LAMP_STAMP_R; dx <= LAMP_STAMP_R; dx++) {
      const r = Math.hypot(dx * LAMP_TEXEL_M, dz * LAMP_TEXEL_M) / LAMP_RADIUS_M;
      if (r >= 1) continue;
      const f = 1 - r * r;
      out[(dz + LAMP_STAMP_R) * LAMP_STAMP_W + (dx + LAMP_STAMP_R)] = Math.round(Math.pow(f, 1.55) * 255);
    }
  }
  return out;
})();

const lampMapData = new Uint8Array(LAMP_MAP_SIZE * LAMP_MAP_SIZE);
const lampMapTexture = new DataTexture(lampMapData, LAMP_MAP_SIZE, LAMP_MAP_SIZE, RedFormat, UnsignedByteType);
lampMapTexture.magFilter = LinearFilter;
lampMapTexture.minFilter = LinearFilter;
lampMapTexture.wrapS = ClampToEdgeWrapping;
lampMapTexture.wrapT = ClampToEdgeWrapping;
lampMapTexture.generateMipmaps = false;
lampMapTexture.needsUpdate = true;

export const lampLight = {
  map: { value: lampMapTexture },
  origin: { value: new Vector2(-LAMP_MAP_EXTENT * 0.5, -LAMP_MAP_EXTENT * 0.5) },
  invExtent: { value: 1 / LAMP_MAP_EXTENT },
};

export const GLSL_LAMP_LIGHT = /* glsl */ `
uniform sampler2D uLampMap;
uniform vec2 uLampOrigin;
uniform float uLampInvExtent;
vec3 streetLampRadiance(vec2 worldXZ, vec3 albedo, float night, float wet) {
  if (night <= 0.005) return vec3(0.0);
  vec2 uv = (worldXZ - uLampOrigin) * uLampInvExtent;
  if (uv.x <= 0.001 || uv.x >= 0.999 || uv.y <= 0.001 || uv.y >= 0.999) return vec3(0.0);
  float l = texture2D(uLampMap, uv).r * night;
  if (l <= 0.002) return vec3(0.0);
  float core = l * l * l;
  float spread = l * (1.35 - 0.35 * l);
  vec3 lampCol = mix(vec3(1.0, 0.48, 0.14), vec3(1.0, 0.62, 0.24), core);
  // Warm streetlamps emit mostly red/amber light that chlorophyll absorbs, so
  // grass and foliage desaturate into a muted warm night earth tone instead of vivid green.
  float greenness = clamp((albedo.g - max(albedo.r, albedo.b)) * 3.5, 0.0, 1.0);
  float warmLuma = dot(albedo, vec3(0.55, 0.35, 0.10)) * (1.0 - 0.38 * greenness);
  vec3 nightAlbedo = mix(albedo, vec3(warmLuma), 0.55 + 0.38 * greenness);
  vec3 litAlbedo = max(nightAlbedo, vec3(0.068, 0.056, 0.044));
  vec3 spec = lampCol * core * (0.02 + 0.14 * wet) * (1.0 - 0.80 * greenness);
  return litAlbedo * lampCol * (spread * 1.45 + core * 0.95) + spec;
}
vec3 streetLampObjectRadiance(vec3 worldPos, vec3 albedo, float night, float roughness, float mask) {
  vec3 faceCross = cross(dFdx(worldPos), dFdy(worldPos));
  if (night <= 0.005 || mask <= 0.005) return vec3(0.0);
  vec2 uv = (worldPos.xz - uLampOrigin) * uLampInvExtent;
  if (uv.x <= 0.001 || uv.x >= 0.999 || uv.y <= 0.001 || uv.y >= 0.999) return vec3(0.0);
  float l = texture2D(uLampMap, uv).r * night;
  if (l <= 0.002) return vec3(0.0);
  vec3 faceN = normalize(faceCross + vec3(0.0, 1e-5, 0.0));
  float overhead = 0.42 + 0.58 * clamp(faceN.y, -0.35, 1.0);
  float core = l * l * l;
  float spread = l * (1.35 - 0.35 * l);
  vec3 lampCol = mix(vec3(1.0, 0.48, 0.14), vec3(1.0, 0.62, 0.24), core);
  float greenness = clamp((albedo.g - max(albedo.r, albedo.b)) * 3.5, 0.0, 1.0);
  float warmLuma = dot(albedo, vec3(0.52, 0.36, 0.12)) * (1.0 - 0.32 * greenness);
  vec3 objAlbedo = mix(albedo, vec3(warmLuma), 0.65 * greenness);
  float gloss = clamp(1.0 - roughness, 0.0, 1.0);
  vec3 spec = lampCol * (core * 0.35 + spread * 0.10) * gloss * gloss * max(0.0, faceN.y);
  return (objAlbedo * lampCol * (spread * 1.25 + core * 0.85) * overhead + spec) * mask;
}
`;

/** Inject overhead streetlamp illumination into a standard shader (characters, driven vehicles). */
export function addStreetLampObjectShader(shader: WebGLProgramParametersWithUniforms): void {
  shader.uniforms.uNight = floraNight;
  shader.uniforms.uLampMap = lampLight.map;
  shader.uniforms.uLampOrigin = lampLight.origin;
  shader.uniforms.uLampInvExtent = lampLight.invExtent;
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nvarying vec3 vLampWorldPos;')
    .replace('#include <worldpos_vertex>', `#include <worldpos_vertex>
#ifdef USE_INSTANCING
  vLampWorldPos = (modelMatrix * instanceMatrix * vec4(transformed, 1.0)).xyz;
#else
  vLampWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
#endif`);
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', `#include <common>\nuniform float uNight;\nvarying vec3 vLampWorldPos;\n${GLSL_LAMP_LIGHT}`)
    .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
  totalEmissiveRadiance += streetLampObjectRadiance(vLampWorldPos, diffuseColor.rgb, uNight, roughnessFactor, 1.0);`);
}

/** Hook a MeshStandardMaterial so it catches overhead streetlamp light at night. */
export function applyStreetLampLight(mat: MeshStandardMaterial): void {
  mat.onBeforeCompile = addStreetLampObjectShader;
}

let dirtyMinRow = 0;
let dirtyMaxRow = -1;

/**
 * Rebuild the world-space streetlamp light map from the active vector chunks.
 * Each chunk provides its scene origin (x, z) and its lamp head (lx, lz) pairs.
 */
export function updateLampLightMap(chunks: Iterable<{ x: number; z: number; lamps: Float32Array | undefined }>): void {
  if (dirtyMaxRow >= dirtyMinRow) {
    lampMapData.fill(0, dirtyMinRow * LAMP_MAP_SIZE, (dirtyMaxRow + 1) * LAMP_MAP_SIZE);
  }
  dirtyMinRow = LAMP_MAP_SIZE;
  dirtyMaxRow = -1;

  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  const list: Array<{ x: number; z: number; lamps: Float32Array }> = [];
  for (const c of chunks) {
    if (c.lamps === undefined || c.lamps.length === 0) continue;
    list.push({ x: c.x, z: c.z, lamps: c.lamps });
    if (c.x < minX) minX = c.x;
    if (c.x + 1400 > maxX) maxX = c.x + 1400;
    if (c.z < minZ) minZ = c.z;
    if (c.z + 1400 > maxZ) maxZ = c.z + 1400;
  }
  if (list.length === 0) {
    lampMapTexture.needsUpdate = true;
    return;
  }
  const cx = (minX + maxX) * 0.5;
  const cz = (minZ + maxZ) * 0.5;
  const ox = Math.round(cx - LAMP_MAP_EXTENT * 0.5);
  const oz = Math.round(cz - LAMP_MAP_EXTENT * 0.5);
  lampLight.origin.value.set(ox, oz);
  const invTexel = 1 / LAMP_TEXEL_M;

  for (let c = 0; c < list.length; c++) {
    const { x: gx, z: gz, lamps } = list[c];
    for (let i = 0; i < lamps.length; i += 2) {
      const lx = lamps[i];
      if (Number.isNaN(lx)) continue;
      const tx = Math.round((gx + lx - ox) * invTexel);
      const tz = Math.round((gz + lamps[i + 1] - oz) * invTexel);
      const r0 = Math.max(0, tz - LAMP_STAMP_R);
      const r1 = Math.min(LAMP_MAP_SIZE - 1, tz + LAMP_STAMP_R);
      const c0 = Math.max(0, tx - LAMP_STAMP_R);
      const c1 = Math.min(LAMP_MAP_SIZE - 1, tx + LAMP_STAMP_R);
      if (r0 > r1 || c0 > c1) continue;
      if (r0 < dirtyMinRow) dirtyMinRow = r0;
      if (r1 > dirtyMaxRow) dirtyMaxRow = r1;
      for (let r = r0; r <= r1; r++) {
        const sr = (r - tz + LAMP_STAMP_R) * LAMP_STAMP_W - tx + LAMP_STAMP_R;
        const rowOff = r * LAMP_MAP_SIZE;
        for (let col = c0; col <= c1; col++) {
          const s = LAMP_STAMP[sr + col];
          if (s === 0) continue;
          const idx = rowOff + col;
          const prev = lampMapData[idx];
          lampMapData[idx] = prev + (((255 - prev) * s) >> 8);
        }
      }
    }
  }
  lampMapTexture.needsUpdate = true;
}

/**
 * A tile's landcover raster, for painting parks, fields and car parks onto the
 * terrain it belongs to. See `core/build/landcover.ts`.
 */
export interface TerrainLandcover {
  texture: Texture;
  /** Ground extent the raster covers, metres. */
  width: number;
  depth: number;
}

/**
 * Weather on the ground, shared by every terrain material: lying snow from
 * real snow-depth observations, and wetness while it rains. Set once a frame.
 */
export const terrainWeather = {
  snow: { value: 0 },
  /** 0..1 snow on the roads: a dusting while it snows, a little along the edges once plowed. */
  roadSnow: { value: 0 },
  /** 0..1 snow on the trees' upper sides, and on everything the flora shader draws (benches, parked cars). */
  treeSnow: { value: 0 },
  /** 0..1 snow on roofs and other upward faces of buildings. */
  roofSnow: { value: 0 },
  wet: { value: 0 },
  /** 0..1 strength of moving cloud shadows on the ground under broken clouds in daylight. */
  cloudShadow: { value: 0 },
  /** Wind-driven cloud drift in world metres. */
  cloudDrift: { value: new Vector2() },
};

/**
 * Snow lying on whatever faces up: a roof, a branch, a bench. The face's
 * slope comes from the screen-space change in its world position, so it
 * works on flat-shaded meshes with no normals. `amount` 0..1 is how much
 * has settled; `lowest` the steepest face (as the up-component of its
 * normal) that still holds some, so steep roofs shed it. The edge of the
 * cover is broken by noise, as snow lies in patches before it is a blanket.
 */
export const GLSL_SNOW_TOP = /* glsl */ `
float snowTopHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float snowTop(vec3 worldPos, float amount, float lowest) {
  if (amount <= 0.005) return 0.0;
  vec3 n = normalize(cross(dFdx(worldPos), dFdy(worldPos)));
  float up = smoothstep(lowest, lowest + 0.3, n.y);
  vec2 p = worldPos.xz * 1.3 + worldPos.y * 0.7;
  vec2 i = floor(p), f = fract(p), w = f * f * (3.0 - 2.0 * f);
  float noise = mix(mix(snowTopHash(i), snowTopHash(i + vec2(1.0, 0.0)), w.x),
                    mix(snowTopHash(i + vec2(0.0, 1.0)), snowTopHash(i + vec2(1.0, 1.0)), w.x), w.y);
  return up * smoothstep(1.0 - amount * 1.15, 1.15 - amount * 1.15, noise);
}
`;

/** Fresh snow's colour on a roof or a branch, in linear light. */
export const SNOW_TOP_COLOUR = 'vec3(0.86, 0.9, 0.96)';

/** How many tunnel portals and trough pieces near the camera cut their mouths out of the terrain. */
export const PORTAL_SLOTS = 16;
/**
 * The portals nearest the camera, in scene metres: per portal, x, z and the
 * way out (dx, dz) in `a`; half-width, length, floor and ceiling in `b`.
 * The terrain inside a portal's walls, over its floor, is not drawn, so the
 * road runs in between them to the opening, nor inside the tube's first
 * metres, under the hill (see `core/build/tunnelmesh.ts`).
 */
export const terrainPortals = {
  a: { value: Array.from({ length: PORTAL_SLOTS }, () => new Vector4()) },
  b: { value: Array.from({ length: PORTAL_SLOTS }, () => new Vector4()) },
  /** x: half the tube's width, to its walls, where the terrain inside it is cut away. */
  c: { value: Array.from({ length: PORTAL_SLOTS }, () => new Vector4()) },
  count: { value: 0 },
};

/** Stand-in for tiles without landcover, so every tile shares one program. */
const NO_LANDCOVER = new DataTexture(new Uint8Array([0]), 1, 1, RedFormat, UnsignedByteType);
NO_LANDCOVER.needsUpdate = true;

/**
 * Landcover looks, by class id — see `LANDCOVER` in `core/sources/shortbread.ts`.
 * Colours in linear light. Patterns are in world metres, so furrows, paving and
 * parking bays keep their size however big the field is.
 */
const GLSL_LANDCOVER = /* glsl */ `
float lcLine(float v, float width) {
  float aa = max(fwidth(v), 0.002);
  return 1.0 - smoothstep(width * 0.5 - aa, width * 0.5 + aa, abs(v));
}
vec3 landcoverColour(int cls, vec2 w) {
  float n = terrainNoise(w * 0.35);
  float fine = terrainHash(floor(w * 1.4));
  if (cls == 1) return vec3(0.085, 0.36, 0.04) * (0.94 + 0.1 * n);
  if (cls == 2) {
    vec3 grass = vec3(0.15, 0.4, 0.05) * (0.9 + 0.2 * n);
    float flower = step(0.955, fine);
    return mix(grass, terrainHash(floor(w * 1.4) + 3.1) > 0.5 ? vec3(0.85, 0.72, 0.15) : vec3(0.85, 0.85, 0.8), flower);
  }
  if (cls == 3) return vec3(0.6, 0.43, 0.11) * (fract(w.x / 1.6) < 0.35 ? 0.86 : 1.0) * (0.95 + 0.08 * n);
  if (cls == 4) return vec3(0.12, 0.34, 0.05) * (fract(w.y / 1.2) < 0.4 ? 0.82 : 1.0);
  if (cls == 5) return vec3(0.22, 0.13, 0.065) * (fract((w.x + w.y) / 0.9) < 0.45 ? 0.78 : 1.0) * (0.9 + 0.15 * n);
  if (cls == 6) return vec3(0.74, 0.6, 0.04) * (fract((w.x - w.y) / 1.4) < 0.3 ? 0.88 : 1.0);
  if (cls == 7) return vec3(0.12, 0.31, 0.055) * (fract(w.x / 5.0) < 0.35 ? 0.78 : 1.0);
  if (cls == 8) {
    float plot = terrainHash(floor(w / vec2(7.0, 11.0)));
    return plot < 0.3 ? vec3(0.12, 0.3, 0.05) : plot < 0.55 ? vec3(0.26, 0.17, 0.08)
      : plot < 0.8 ? vec3(0.2, 0.36, 0.07) : vec3(0.48, 0.38, 0.1);
  }
  if (cls == 9) return vec3(0.72, 0.6, 0.38) * (0.95 + 0.08 * n);
  if (cls == 10) return vec3(0.4, 0.38, 0.35) * (0.85 + 0.3 * terrainNoise(w * 0.9));
  if (cls == 11) {
    float puddle = smoothstep(0.62, 0.66, terrainNoise(w * 0.12) * 0.7 + terrainNoise(w * 0.5) * 0.3);
    return mix(vec3(0.075, 0.19, 0.06) * (0.9 + 0.2 * n), vec3(0.04, 0.14, 0.2), puddle);
  }
  if (cls == 12) return mix(vec3(0.2, 0.25, 0.09), vec3(0.28, 0.11, 0.22), step(0.8, fine)) * (0.9 + 0.2 * n);
  if (cls == 13) return vec3(0.065, 0.28, 0.04) * (0.94 + 0.1 * n);
  if (cls == 14) return vec3(0.6, 0.46, 0.28) * (0.95 + 0.08 * n);
  if (cls == 15) return vec3(0.08, 0.38, 0.04) * (mod(floor(w.x / 5.0), 2.0) < 1.0 ? 0.9 : 1.03);
  if (cls == 16) {
    // Car park asphalt. The bays are marked on it as surfaces, laid out
    // along the lot where its cars park (see core/build/parking.ts).
    return vec3(0.14, 0.15, 0.16) * (0.95 + 0.08 * n);
  }
  if (cls == 17) {
    vec2 cell = w / 0.9;
    float joint = max(lcLine(fract(cell.x) - 0.5, 0.08), lcLine(fract(cell.y) - 0.5, 0.08));
    return vec3(0.6, 0.55, 0.46) * (0.94 + 0.08 * terrainHash(floor(cell))) * (1.0 - 0.15 * joint);
  }
  if (cls == 18) return vec3(0.3, 0.22, 0.13) * (0.85 + 0.3 * terrainNoise(w * 0.7));
  if (cls == 19) return vec3(0.3, 0.29, 0.27) * (0.85 + 0.3 * terrainHash(floor(w * 5.0)));
  if (cls == 20) {
    vec2 slab = w / 4.0;
    float joint = max(lcLine(fract(slab.x) - 0.5, 0.02), lcLine(fract(slab.y) - 0.5, 0.02));
    return vec3(0.33, 0.33, 0.32) * (0.95 + 0.06 * n) * (1.0 - 0.2 * joint);
  }
  if (cls == 22) {
    // Town ground: pale concrete flags, a little uneven in tone, as a
    // pavement and a courtyard are.
    vec2 flag = w / vec2(1.8, 1.2);
    float joint = max(lcLine(fract(flag.x) - 0.5, 0.03), lcLine(fract(flag.y) - 0.5, 0.045));
    float tone = 0.93 + 0.07 * terrainHash(floor(flag)) + 0.05 * n;
    return vec3(0.4, 0.39, 0.37) * tone * (1.0 - 0.18 * joint);
  }
  if (cls == 23) {
    // A mown garden lawn: brighter than the verge, with faint mowing stripes.
    float stripe = mod(floor((w.x + w.y) / 1.6), 2.0) < 1.0 ? 0.95 : 1.03;
    return vec3(0.16, 0.56, 0.06) * stripe * (0.96 + 0.06 * n);
  }
  if (cls == 24) return vec3(0.15, 0.155, 0.16) * (0.93 + 0.1 * terrainHash(floor(w * 3.0)));
  return vec3(0.12, 0.45, 0.05) * (0.94 + 0.08 * n);
}
`;

export interface TerrainMaterialOptions {
  /** Draw both faces, so a winding mistake is visible rather than invisible. */
  debugDoubleSided?: boolean;
  wireframe?: boolean;
  /** Paint this tile's landcover raster onto it. */
  landcover?: TerrainLandcover;
}

export function createTerrainMaterial(options: TerrainMaterialOptions = {}): MeshStandardMaterial {
  const material = new MeshStandardMaterial({
    color: new Color(0xffffff),
    roughness: 0.95,
    metalness: 0,
    side: options.debugDoubleSided === true ? DoubleSide : FrontSide,
    wireframe: options.wireframe === true,
    flatShading: true,
    vertexColors: true,
  });

  const landcover = options.landcover;
  material.onBeforeCompile = (shader: WebGLProgramParametersWithUniforms) => {
    shader.uniforms.uLandcover = { value: landcover?.texture ?? NO_LANDCOVER };
    shader.uniforms.uLandcoverExtent = { value: new Vector2(landcover?.width ?? 1, landcover?.depth ?? 1) };
    shader.uniforms.uLandcoverOn = { value: landcover === undefined ? 0 : 1 };
    shader.uniforms.uSnowCover = terrainWeather.snow;
    shader.uniforms.uRoadSnow = terrainWeather.roadSnow;
    shader.uniforms.uWet = terrainWeather.wet;
    shader.uniforms.uCloudShadow = terrainWeather.cloudShadow;
    shader.uniforms.uCloudDrift = terrainWeather.cloudDrift;
    shader.uniforms.uNight = floraNight;
    shader.uniforms.uLampMap = lampLight.map;
    shader.uniforms.uLampOrigin = lampLight.origin;
    shader.uniforms.uLampInvExtent = lampLight.invExtent;
    shader.uniforms.uPortalA = terrainPortals.a;
    shader.uniforms.uPortalB = terrainPortals.b;
    shader.uniforms.uPortalC = terrainPortals.c;
    shader.uniforms.uPortals = terrainPortals.count;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>
varying vec3 vWorldPos;
varying vec2 vLocal;
attribute vec4 aGround;
attribute vec4 aPatch;
attribute vec4 aRock;
varying vec4 vGround;
varying vec4 vPatch;
varying vec3 vRock;`)
      .replace(
        '#include <worldpos_vertex>',
        `#include <worldpos_vertex>
  vWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;
  vLocal = transformed.xz;
  vGround = aGround;
  vPatch = aPatch;
  vRock = aRock.rgb;`,
      );

    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
varying vec3 vWorldPos;
varying vec2 vLocal;
uniform sampler2D uLandcover;
uniform vec2 uLandcoverExtent;
uniform float uLandcoverOn;
uniform float uSnowCover;
uniform float uRoadSnow;
uniform float uWet;
uniform float uCloudShadow;
uniform vec2 uCloudDrift;
uniform float uNight;
uniform vec4 uPortalA[${PORTAL_SLOTS}];
uniform vec4 uPortalB[${PORTAL_SLOTS}];
uniform vec4 uPortalC[${PORTAL_SLOTS}];
uniform int uPortals;
varying vec4 vGround;
varying vec4 vPatch;
varying vec3 vRock;
float terrainHash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
float terrainNoise(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 w = f * f * (3.0 - 2.0 * f);
  return mix(mix(terrainHash(i), terrainHash(i + vec2(1.0, 0.0)), w.x),
             mix(terrainHash(i + vec2(0.0, 1.0)), terrainHash(i + vec2(1.0, 1.0)), w.x), w.y);
}
${GLSL_LANDCOVER}
${GLSL_LAMP_LIGHT}`)
      .replace(
        '#include <color_fragment>',
        `#include <color_fragment>
        for (int i = 0; i < ${PORTAL_SLOTS}; i++) {
          if (i >= uPortals) break;
          vec2 rel = vWorldPos.xz - uPortalA[i].xy;
          float along = dot(rel, uPortalA[i].zw);
          float across = abs(rel.x * -uPortalA[i].w + rel.y * uPortalA[i].z);
          if (vWorldPos.y < uPortalB[i].z - 0.3) continue;
          // Out in the mouth, between its walls, everything over the floor;
          // in the tube, out to its walls, up to its roof. Cut to the road
          // alone, the ground showed between it and the walkways' walls.
          if ((across < uPortalB[i].x && along > -0.05 && along < uPortalB[i].y) ||
              (across < uPortalC[i].x && along <= -0.05 && along > -${PORTAL_INSIDE}.0 && vWorldPos.y < uPortalB[i].w)) discard;
        }
        float wetRoughness = 0.95;
        {
          // The palette arrives per vertex from the biome model — see
          // core/biome/palette.ts. This only decides how it is laid out:
          // patches, dune bands, rock on slopes and snow on the flats.
          vec3 face = normalize(cross(dFdx(vWorldPos), dFdy(vWorldPos)));
          float slope = 1.0 - abs(face.y);

          // Field-sized patches: two octaves of value noise in world metres.
          vec2 cell = vWorldPos.xz / 170.0;
          float patchiness = terrainNoise(cell) * 0.7 + terrainNoise(cell * 3.1) * 0.3;

          vec3 ground = vGround.rgb;
          vec3 patchTone = vPatch.rgb;
          vec3 flat_ = mix(ground * 0.86, ground, smoothstep(0.15, 0.45, patchiness));
          flat_ = mix(flat_, patchTone, smoothstep(0.55, 0.85, patchiness));

          // Wind-blown dune bands, where the biome asks for them: the patch
          // colour runs in long wavy stripes across the sand.
          float dunes = vPatch.a;
          if (dunes > 0.01) {
            float wave = vWorldPos.x * 0.021 + vWorldPos.z * 0.009
              + terrainNoise(vWorldPos.xz / 260.0) * 5.0;
            float band = smoothstep(0.35, 0.95, sin(wave * 6.2831) * 0.5 + 0.5);
            flat_ = mix(flat_, patchTone, band * dunes * 0.75);
          }

          vec3 steep = mix(vRock, vRock * 1.12, terrainNoise(vWorldPos.xz / 40.0));
          // Slopes turn to rock quickly: anything past ~30 degrees reads as
          // exposed ground.
          vec3 colour = mix(flat_, steep, smoothstep(0.2, 0.58, slope));

          // Landcover from the map — parks, fields, car parks — painted over the
          // biome ground. The raster holds class ids, so neighbouring pixels are
          // blended by class here rather than by texture filtering, which keeps
          // edges smooth instead of stepped at 1.3 m a pixel.
          // How much of here is a car park: plowed in winter, as the roads are.
          float lcParking = 0.0;
          if (uLandcoverOn > 0.5) {
            ivec2 size = textureSize(uLandcover, 0);
            vec2 p = vLocal / uLandcoverExtent * vec2(size) - 0.5;
            ivec2 i0 = ivec2(floor(p));
            vec2 f = fract(p);
            vec3 sum = vec3(0.0);
            float covered = 0.0;
            float quay = 0.0;
            for (int k = 0; k < 4; k++) {
              ivec2 o = ivec2(k & 1, k >> 1);
              ivec2 t = clamp(i0 + o, ivec2(0), size - 1);
              int cls = int(texelFetch(uLandcover, t, 0).r * 255.0 + 0.5);
              float w = (o.x == 1 ? f.x : 1.0 - f.x) * (o.y == 1 ? f.y : 1.0 - f.y);
              // A city's canal between its quays: no ground drawn in it, where
              // the ground is kept at the street's level up to the wall (build/quays.ts).
              if (cls == ${LANDCOVER.quayWater}) { quay += w; continue; }
              if (${PLOWED_LANDCOVER.map(c => `cls == ${c}`).join(' || ')}) lcParking += w;
              if (cls > 0) {
                sum += landcoverColour(cls, vWorldPos.xz) * w;
                covered += w;
              }
            }
            // Out to the wall and a little past, under its coping: the raster's
            // edge, 1.3 m a pixel, wanders either side of the wall's line.
            if (quay > 0.2) discard;
            if (covered > 0.001) {
              colour = mix(colour, sum / covered, covered * (1.0 - smoothstep(0.55, 0.8, slope)));
            }
          }

          // Snow settles on the flats and slides off steep rock. Its edge is
          // broken by noise so a snow line reads as drifts, not a contour.
          // The season's snow, or the real snow on the ground today if deeper.
          float snow = max(vGround.a, uSnowCover);
          float drift = snow + (terrainNoise(vWorldPos.xz / 55.0) - 0.5) * 0.35;
          float lying = smoothstep(0.35, 0.6, drift) * (1.0 - smoothstep(0.5, 0.82, slope) * (1.0 - snow * 0.55));
          // Car parks are plowed; the season's snow (vGround.a) stays on them
          // only as a dusting, and they take the roads' patchy dusting while
          // it snows (see roadMaterial.ts).
          lying *= 1.0 - 0.85 * lcParking;
          if (lcParking > 0.01 && uRoadSnow > 0.005) {
            float n = terrainNoise(vWorldPos.xz * 0.7) * 0.6 + terrainNoise(vWorldPos.xz * 2.9) * 0.4;
            lying = max(lying, smoothstep(1.0 - uRoadSnow, 1.15 - uRoadSnow, n) * lcParking);
          }
          colour = mix(colour, vec3(0.86, 0.9, 0.96), lying);

          // Rain darkens the ground, as wet earth and grass do, and gives flat ground a damp sheen.
          colour *= 1.0 - 0.22 * uWet;
          if (uWet > 0.005) {
            float damp = uWet * (1.0 - lying * 0.85) * (1.0 - smoothstep(0.25, 0.7, slope));
            wetRoughness = mix(0.95, 0.34 + 0.30 * patchiness, damp);
          }

          // Moving cloud shadows drifting downwind across the landscape in broken cloud.
          if (uCloudShadow > 0.01) {
            float cPatch = terrainNoise((vWorldPos.xz - uCloudDrift) * 0.0032);
            colour *= 1.0 - smoothstep(0.44, 0.74, cPatch) * uCloudShadow * 0.26;
          }
          // At night, mesopic vision and warm streetlamps mute vivid daytime grass greens.
          if (uNight > 0.005) {
            float greenExcess = clamp((colour.g - max(colour.r, colour.b)) * 3.0, 0.0, 1.0);
            float nightLuma = dot(colour, vec3(0.42, 0.44, 0.14)) * (1.0 - 0.25 * greenExcess);
            colour = mix(colour, vec3(nightLuma), uNight * greenExcess * 0.72);
          }
          diffuseColor.rgb *= colour;
        }`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
        roughnessFactor = wetRoughness;`,
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
        totalEmissiveRadiance += streetLampRadiance(vWorldPos.xz, diffuseColor.rgb, uNight, uWet);`,
      );
  };

  return material;
}
